import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  FormControlLabel,
  Divider,
  IconButton,
} from '@mui/material';
import {
  TextField,
  DatePicker,
  SwitchButton,
  Select,
} from '../../../components';
import dayjs from 'dayjs';
import { Staff } from './Staff';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateOrgFaculty } from './graphql/useCreateOrgFaculty';
import { useGetOrgFaculty } from './graphql/useGetOrgFaculty';
import { useUpdateOrgFaculty } from './graphql/useUpdateOrgFaculty';
import { Alert } from '../../../components/Alert';

export const FacultyForm: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Mode detection: Add or Edit
  const isEditMode = id != null;

  // GraphQL hooks
  const { data, loading: loadingGet } = useGetOrgFaculty(id ?? '');
  const { createOrgFaculty, loading: creating } = useCreateOrgFaculty();
  const { updateOrgFaculty, loading: updating } = useUpdateOrgFaculty();

  const initializeStaff = (): any => ({
    netId: '',
    email: '',
    surname: '',
    givenName: '',
    title: '',
    department: '',
    effectiveFrom: null as dayjs.Dayjs | null,
    effectiveTo: null as dayjs.Dayjs | null,
  });

  const [school, setSchool] = useState<boolean>(false);
  const [frcSecretariesList, setFrcSecretariesList] = useState<any[]>([
    initializeStaff(),
  ]);
  const [formValues, setFormValues] = useState({
    abbreviation: '',
    title: '',
    programmeCode: '',
    effectiveFrom: null as dayjs.Dayjs | null,
    effectiveTo: null as dayjs.Dayjs | null,
    isActive: true,
    dean: initializeStaff(),
    frcChair: initializeStaff(),
    frcSecretaries: [initializeStaff()],
  });

  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<{
    alertContent: string;
    alertType: 'success' | 'error';
  }>({
    alertContent: '',
    alertType: 'success',
  });

  // Fetch data if in Edit mode
  useEffect(() => {
    if (isEditMode && data?.getOrgFaculty != null) {
      // Populate form with existing data
      setFormValues({
        abbreviation: data.getOrgFaculty.abbreviation,
        title: data.getOrgFaculty.title,
        programmeCode: data.getOrgFaculty.programmeCode ?? '',
        effectiveFrom: data.getOrgFaculty.effectiveFrom ?? null,
        effectiveTo: data.getOrgFaculty.effectiveTo ?? null,
        isActive: data.getOrgFaculty.isActive,
        dean: data.getOrgFaculty.dean ?? initializeStaff(),
        frcChair: data.getOrgFaculty.frcChair ?? initializeStaff(),
        frcSecretaries:
          data.getOrgFaculty.frcSecretaries.length > 0
            ? data.getOrgFaculty.frcSecretaries
            : [initializeStaff()],
      });
      setSchool(data.getOrgFaculty.school ?? false);
      setFrcSecretariesList(
        data.getOrgFaculty.frcSecretaries.length > 0
          ? data.getOrgFaculty.frcSecretaries
          : [initializeStaff()],
      );
    } else if (loadingGet === false && data == null && isEditMode) {
      // If id is provided but data is not found, navigate back
      navigate('/internal/super-admin/organization-management');
    }
  }, [data, isEditMode, loadingGet, navigate]);

  const formik = useFormik({
    initialValues: formValues,
    enableReinitialize: true,
    onSubmit: async values => {
      try {
        const filterStaffList = (staffList: any[]): any[] =>
          staffList.filter(
            staff =>
              Object.keys(staff).length > 0 &&
              Object.values(staff).some(
                value => value !== '' && value !== null && value !== undefined,
              ),
          );

        const filteredDean = filterStaffList([values.dean])[0] ?? null;
        const filteredFrcChair = filterStaffList([values.frcChair])[0] ?? null;
        const filteredSecretaries = filterStaffList(frcSecretariesList);

        if (isEditMode) {
          // Edit mode: update existing faculty
          await updateOrgFaculty({
            variables: {
              facultyId: id,
              input: {
                abbreviation: values.abbreviation,
                title: values.title,
                effectiveFrom: values.effectiveFrom,
                effectiveTo: values.effectiveTo,
                school,
                isActive: values.isActive,
                dean: filteredDean,
                frcChair: filteredFrcChair,
                frcSecretaries: filteredSecretaries,
              },
            },
          });

          setAlertDetails({
            alertContent: 'Faculty edited successfully!',
            alertType: 'success',
          });
          setOpenAlert(true);
        } else {
          // Add mode: create new faculty
          await createOrgFaculty({
            variables: {
              input: {
                abbreviation: values.abbreviation,
                title: values.title,
                effectiveFrom: values.effectiveFrom,
                effectiveTo: values.effectiveTo,
                school,
                isActive: values.isActive,
                dean: filteredDean,
                frcChair: filteredFrcChair,
                frcSecretaries: filteredSecretaries,
              },
            },
          });

          navigate('/internal/super-admin/organization-management', {
            state: {
              alertContent: 'Faculty added successfully!',
              alertType: 'success',
            },
          });
        }
      } catch (err) {
        // Handle error
        setAlertDetails({
          alertContent: `Error Occurs: ${
            err instanceof Error ? err.message : 'Unknown error'
          }`,
          alertType: 'error',
        });
        setOpenAlert(true);
      }
    },
  });

  const addStaff = (
    staffListSetter: React.Dispatch<React.SetStateAction<any[]>>,
  ): void => {
    staffListSetter(prev => [...prev, initializeStaff()]);
  };

  const clearStaffData = (staffType: string): void => {
    setFormValues(prev => ({
      ...prev,
      [staffType]: initializeStaff(),
    }));
  };

  const clearStaffListItem = (
    index: number,
    staffList: any[],
    staffListSetter: React.Dispatch<React.SetStateAction<any[]>>,
  ): void => {
    if (staffList.length > 1) {
      staffListSetter(prev => prev.filter((_, i) => i !== index));
    } else {
      staffListSetter(prev =>
        prev.map((staff, i) => (i === index ? initializeStaff() : staff)),
      );
    }
  };

  const updateStaffListItem = (
    index: number,
    updatedStaff: any,
    staffListSetter: React.Dispatch<React.SetStateAction<any[]>>,
  ): void => {
    staffListSetter(prev =>
      prev.map((staff, i) => (i === index ? updatedStaff : staff)),
    );
  };

  const handleCancel = (): void => {
    navigate('/internal/super-admin/organization-management');
  };

  if (loadingGet === true || creating === true || updating === true)
    return <div>Loading...</div>;

  return (
    <Box p={4}>
      <Typography variant="h4" mb={4}>
        {isEditMode ? 'Edit Faculty' : 'Add Faculty'}
      </Typography>
      <FormControlLabel
        control={
          <SwitchButton
            checked={school}
            onChange={() => {
              setSchool(!school);
            }}
          />
        }
        label={school ? 'Switch to Faculty' : 'Switch to School'}
      />
      <Box mt={2} />
      <form onSubmit={formik.handleSubmit} noValidate>
        {/* Faculty/School Basic Information */}
        <Box mb={4} p={3} border={1} borderColor="grey.300" borderRadius="8px">
          <Typography variant="h6" mb={2}>
            {school ? 'School' : 'Faculty'} Basic Information (Required *)
          </Typography>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextField
                fullWidth
                label="Abbreviation"
                name="abbreviation"
                value={formValues.abbreviation}
                onChange={e => {
                  setFormValues({
                    ...formValues,
                    abbreviation: e.target.value,
                  });
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextField
                fullWidth
                label="Title"
                name="title"
                value={formValues.title}
                onChange={e => {
                  setFormValues({ ...formValues, title: e.target.value });
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <DatePicker
                label="Effective From"
                name="effectiveFrom"
                value={
                  formValues.effectiveFrom !== null
                    ? dayjs(formValues.effectiveFrom)
                    : null
                }
                onChange={date => {
                  setFormValues({
                    ...formValues,
                    effectiveFrom: date as dayjs.Dayjs | null,
                  });
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <DatePicker
                label="Effective To"
                name="effectiveTo"
                value={
                  formValues.effectiveTo !== null
                    ? dayjs(formValues.effectiveTo)
                    : null
                }
                onChange={date => {
                  setFormValues({
                    ...formValues,
                    effectiveTo: date as dayjs.Dayjs | null,
                  });
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Select
                label="Status"
                name="isActive"
                value={formValues.isActive ? 'Active' : 'Inactive'}
                options={[
                  { value: 'Active', label: 'Active' },
                  { value: 'Inactive', label: 'Inactive' },
                ]}
                onChange={e => {
                  setFormValues({
                    ...formValues,
                    isActive: e.target.value === 'Active',
                  });
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Dean Section */}
        <Box mb={4} p={3} border={1} borderColor="grey.300" borderRadius="8px">
          <Typography variant="h6" mb={2}>
            {school ? 'Dean of School' : 'Dean of Faculty'}
          </Typography>
          <Box display="flex" alignItems="center">
            <Staff
              prefix="dean"
              staffData={formValues.dean}
              onStaffChange={updatedStaff => {
                setFormValues({ ...formValues, dean: updatedStaff });
              }}
            />
            <IconButton
              aria-label="clear"
              onClick={() => {
                clearStaffData('dean');
              }}
              sx={{ ml: 2 }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>

        {/* FRC Chair Section */}
        <Box mb={4} p={3} border={1} borderColor="grey.300" borderRadius="8px">
          <Typography variant="h6" mb={2}>
            {school
              ? 'School Board (SB) Chair'
              : 'Faculty Research Committee (FRC) Chair'}
          </Typography>
          <Box display="flex" alignItems="center">
            <Staff
              prefix="frcChair"
              staffData={formValues.frcChair}
              onStaffChange={updatedStaff => {
                setFormValues({ ...formValues, frcChair: updatedStaff });
              }}
            />
            <IconButton
              aria-label="clear"
              onClick={() => {
                clearStaffData('frcChair');
              }}
              sx={{ ml: 2 }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>

        {/* FRC Secretaries Section */}
        <Box mb={4} p={3} border={1} borderColor="grey.300" borderRadius="8px">
          <Typography variant="h6" mb={2}>
            {school
              ? 'School Board (SB) Secretary'
              : 'Faculty Research Committee (FRC) Secretary'}
          </Typography>
          {frcSecretariesList.map((secretary, index) => (
            <React.Fragment key={index}>
              <Box mb={2} display="flex" alignItems="center">
                <Staff
                  prefix={`frcSecretaries[${index}]`}
                  staffData={secretary}
                  onStaffChange={updatedStaff => {
                    updateStaffListItem(
                      index,
                      updatedStaff,
                      setFrcSecretariesList,
                    );
                  }}
                />
                <IconButton
                  aria-label="delete"
                  onClick={() => {
                    clearStaffListItem(
                      index,
                      frcSecretariesList,
                      setFrcSecretariesList,
                    );
                  }}
                  sx={{ ml: 2 }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              {index < frcSecretariesList.length - 1 && (
                <Divider sx={{ my: 4 }} />
              )}
            </React.Fragment>
          ))}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              addStaff(setFrcSecretariesList);
            }}
          >
            Add Secretary
          </Button>
        </Box>

        {/* Submit Button */}
        <Button variant="contained" color="primary" type="submit">
          {isEditMode ? 'Update' : 'Submit'}
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleCancel}
          sx={{ ml: 2 }}
        >
          Cancel
        </Button>
      </form>
      {/* Alert Component */}
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
    </Box>
  );
};
